// REMIX HMR BEGIN
import * as __hmr__ from "remix:hmr";
if (import.meta) {
import.meta.hot = __hmr__.createHotContext(
//@ts-expect-error
"app/components/helpers/componentResolver.tsx"
);
import.meta.hot.lastModified = "1740183864615.2917";
}
// REMIX HMR END

import { NodePage, ParagraphLogoStrip, ParagraphUnion } from "~/@types/gen";
import {
  ParagraphCardGroup,
  ParagraphCodeBlock,
  ParagraphHero,
  ParagraphHeroImage,
  ParagraphImage,
  ParagraphResponsiveImage,
  ParagraphStaticComponent,
  ParagraphText,
  ParagraphViewReference,
  ParagraphTestimonial,
  ParagraphStaticForm,
  isSupportedForm,
} from "~/components/paragraph";
import ContentWrapper from "~/components/ContentWrapper";
import ParagraphLogoStripGroup from "~/components/paragraph/ParagraphLogoStripGroup";
import clsx from "clsx";

import ParagraphAccordion from "../paragraph/ParagraphAccordion";
import VisualEditorComponentContainer from "./VisualEditorComponentContainer";

import { Layout } from "~/@types/helpers";

type Components = NodePage["components"];

type Paragraphs = Exclude<
  ParagraphUnion["__typename"],
  | "ParagraphCard"
  | "ParagraphStaticComponent"
  | "ParagraphLogoStrip"
  | "ParagraphLogoStripItem"
  | "ParagraphResponsiveImage"
  | "ParagraphStaticForm"
  | "ParagraphCardReference"
>;

const BACKGROUND_TOKENS = {
  PRIMARY: "bg-primary-700",
  GRADIENT_PRIMARY:
    "bg-gradient-primary animate-background-transition bg-400% md:bg-none md:animate-none md:bg-[size:100%] md:bg-neutral-100 overflow-x-hidden",
  GRADIENT_HERO: "bg-gradient-primary animate-background-transition bg-400%",
  NEUTRAL: "bg-neutral-100",
  WHITE: "bg-white",
};

const wrapperClassName: Record<Paragraphs, Record<string, string>> = {
  ParagraphHero: {
    primary: BACKGROUND_TOKENS.GRADIENT_PRIMARY,
    secondary: BACKGROUND_TOKENS.NEUTRAL,
    tertiary: BACKGROUND_TOKENS.GRADIENT_HERO,
  },
  ParagraphHeroImage: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.GRADIENT_HERO,
    tertiary: BACKGROUND_TOKENS.WHITE,
  },
  ParagraphCardGroup: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.WHITE,
    tertiary: BACKGROUND_TOKENS.WHITE,
    quaternary: BACKGROUND_TOKENS.WHITE
  },
  ParagraphText: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.PRIMARY,
    tertiary: BACKGROUND_TOKENS.WHITE,
  },
  ParagraphCodeBlock: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.NEUTRAL,
  },
  ParagraphImage: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.NEUTRAL,
  },
  ParagraphViewReference: {
    primary: BACKGROUND_TOKENS.PRIMARY,
    secondary: BACKGROUND_TOKENS.WHITE,
  },
  ParagraphLogoStripGroup: {
    primary: BACKGROUND_TOKENS.PRIMARY,
    secondary: BACKGROUND_TOKENS.PRIMARY,
  },
  ParagraphAccordion: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.NEUTRAL,
  },
  ParagraphTestimonial: {
    primary: BACKGROUND_TOKENS.NEUTRAL,
    secondary: BACKGROUND_TOKENS.PRIMARY,
  },
} as const;

const backgroundClassName = {
  lighter: BACKGROUND_TOKENS.WHITE,
  light: BACKGROUND_TOKENS.NEUTRAL,
  dark: BACKGROUND_TOKENS.PRIMARY,
} as const;

type BackgroundColorMap = keyof typeof backgroundClassName;

const resolve = (
  component: ParagraphUnion | undefined,
  wrapperVariant: Layout
) => {
  if (!component) {
    return null;
  }

  if (component.__typename === "ParagraphHero") {
    return (
      <ContentWrapper
        className={clsx(
          wrapperClassName.ParagraphHero[component.variant],
          component.variant === "primary" ? "!pb-0" : ""
        )}
        spacing={wrapperVariant}
      >
        <ParagraphHero
          cta={component.cta}
          text={component.text}
          title={component.title}
          variant={component.variant}
        />
      </ContentWrapper>
    );
  }
  if (component.__typename === "ParagraphHeroImage") {
    return (
      <ContentWrapper
        className={wrapperClassName.ParagraphHeroImage[component.variant]}
        spacing={wrapperVariant}
      >
        <ParagraphHeroImage
          image={component.image}
          cta={component.cta}
          variant={component.variant}
          alignment={component.alignment}
          subtitle={component.subtitle}
          text={component.text}
          title={component.title}
        />
      </ContentWrapper>
    );
  }
  if (component.__typename === "ParagraphText") {
    const background = component.background as BackgroundColorMap;
    return (
      <ContentWrapper className={clsx(backgroundClassName[background])}
        spacing={wrapperVariant}
      >
        <ParagraphText
          text={component.text}
          subtitle={component.subtitle}
          titleOptional={component.titleOptional}
          variant={component.variant}
          background={background}
        />
      </ContentWrapper>
    );
  }
  if (component.__typename === "ParagraphCardGroup") {
    return (
      <ContentWrapper
        className={wrapperClassName.ParagraphCardGroup[component.variant]}
        spacing={wrapperVariant}
      >
        <ParagraphCardGroup
          columns={component.columns}
          items={component.items}
          textOptional={component.textOptional}
          titleOptional={component.titleOptional}
          variant={component.variant}
        />
      </ContentWrapper>
    );
  }
  if (component.__typename === "ParagraphStaticComponent") {
    return <ParagraphStaticComponent component={component.component} />;
  }

  if (component.__typename === "ParagraphCodeBlock") {
    return (
      <ContentWrapper
        className={wrapperClassName.ParagraphCodeBlock["primary"]}
        spacing={wrapperVariant}
      >
        <ParagraphCodeBlock
          code={component.code}
          language={component.language}
          showLineNumbers={component.showLineNumbers}
          textOptional={component.textOptional}
          titleOptional={component.titleOptional}
        />
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphResponsiveImage") {
    const background = component.background as BackgroundColorMap;
    const backgroundColor =
      backgroundClassName[background] || backgroundClassName.lighter;
    return (
      <ContentWrapper
        className={clsx(backgroundColor)}
        spacing={wrapperVariant}
      >
        <ParagraphResponsiveImage
          titleOptional={component.titleOptional}
          subtitle={component.subtitle}
          images={component.images}
          breakpointChange={component.breakpointChange}
          background={background}
        />
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphImage") {
    return (
      <ContentWrapper
        className={wrapperClassName.ParagraphImage["primary"]}
        spacing={wrapperVariant}
      >
        <ParagraphImage
          image={component.image}
          textOptional={component.textOptional}
          titleOptional={component.titleOptional}
        />
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphViewReference") {
    return (
      <ContentWrapper
        className={wrapperClassName.ParagraphViewReference["secondary"]}
        spacing={"primary"}
      >
        <ParagraphViewReference
          display={component.reference.display}
          items={component.reference.results}
          text={component.text}
          title={component.title}
          ctaSingle={component.ctaSingle}
        />
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphLogoStripGroup") {
    return (
      <ContentWrapper
        className={wrapperClassName.ParagraphLogoStripGroup["primary"]}
        spacing={wrapperVariant}
      >
        <ParagraphLogoStripGroup
          title={component.title}
          items={component.items as ParagraphLogoStrip[]}
          textOptional={component.textOptional}
        />
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphAccordion") {
    return (
      <ContentWrapper
        spacing={wrapperVariant}
        className={wrapperClassName.ParagraphAccordion["primary"]}
      >
        <ParagraphAccordion items={component.items} title={component.title} />
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphTestimonial") {
    const background = component.backgroundRequired as BackgroundColorMap;

    return (
      <ContentWrapper
        spacing={wrapperVariant}
        className={`${backgroundClassName[background as keyof typeof backgroundClassName]}`}
      >
        <ParagraphTestimonial 
          titleOptional={component.titleOptional} 
          name={component.name} 
          quote={component.quote} 
          position={component.position} 
          company={component.company} 
          imageOptional={component.imageOptional}
          background={background}/>          
      </ContentWrapper>
    );
  }

  if (component.__typename === "ParagraphStaticForm") {
    if (!isSupportedForm(component.form)) {
      return null;
    }

    return (
      <ContentWrapper
        spacing={wrapperVariant}
        className={backgroundClassName.lighter}
      >
        <ParagraphStaticForm
          title={component.title}
          form={component.form}
          preSelectedValue={component.preSelectedValue}
          imageOptional={component.imageOptional}
          postActionText={component.postActionText}
          textOptional={component.textOptional}
        />
      </ContentWrapper>
    );
  }  

  return <p>Unknown component</p>;
};

export const componentResolver = (
  data: Components = [],
  wrapperVariant: Layout,
  environment: string
) => {
  return data.map((component) => {
    const reactComponent = resolve(component, wrapperVariant);
    if (environment === "preview") {
      return (
        <VisualEditorComponentContainer
          action="edit"
          storage="paragraph"
          uuid={component.id}
        >
          {reactComponent}
        </VisualEditorComponentContainer>
      );
    }

    return reactComponent;
  });
};

export default componentResolver;
